import {
  ConfirmationDialog,
  ConfirmationDialogModel
} from "../../share/dialog/confirmation-dialog/confirmation.dialog";
import { forkJoin } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { AddBranchDialogComponent } from "./add-branch-dialog/add-branch-dialog.component";
import { AddUserDialogComponent } from "./add-user-dialog/add-user-dialog.component";
import { AlertService } from "../../share/service/alert.service";
import { FormBuilder } from "@angular/forms";
import { O2OIndustrySectorService } from "../services/o2o-industry-sector.service";
import { O2OPartnerService } from "../services/o2o-partner.service";
import { ActivatedRoute, Router } from "@angular/router";
import { O2OPartnerBranchService } from "../services/o2o-partner-branch.service";
import {
  O2ODocumentInterface,
  O2OPartnerBranchListingInterface,
  O2OPartnerDetailsInterface,
  O2OPartnerEditInterface,
  O2OPartnerProtectionListingInterface,
  O2OPartnerUserListingInterface
} from "../interfaces/o2o-partner.interface";
import { O2OPartnerUserService } from "../services/o2o-partner-user.service";
import { ImageViewerDialogComponent } from "../components/image-viewer-dialog/image-viewer-dialog.component";
import { AddProtectionDialogComponent } from "./add-protection-dialog/add-protection-dialog.component";
import { O2OPartnerProtectionService } from "../services/o2o-partner-protection.service";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { PartnerSaveComponent } from "../partner-save/partner-save.component";
import { ValidatorFunction } from "../../share/function/validator.function";
import { PartnerStatus } from "../../share/constants/partner-platform/partner-status";
import { PageEvent } from "@angular/material/paginator";

@Component({
  selector: "app-partner-details",
  templateUrl: "./partner-details.component.html",
  styleUrls: ["./partner-details.component.scss"]
})
export class PartnerDetailsComponent
  extends PartnerSaveComponent
  implements OnInit, OnDestroy
{
  id = this.route.snapshot.paramMap.get("id");
  details: O2OPartnerDetailsInterface;
  branchTableConfig: BranchTableConfigInterface = {
    id: TableConfigType.Branch,
    limit: 10,
    offset: 1,
    length: 0,
    data: [],
    displayedColumns: [
      "branchName",
      "address",
      "postcode",
      "state",
      "country",
      "status",
      "actions"
    ]
  };
  userTableConfig: UserTableConfigInterface = {
    id: TableConfigType.User,
    limit: 10,
    offset: 1,
    length: 0,
    data: [],
    displayedColumns: [
      "userName",
      "mobileNo",
      "email",
      "branch",
      "role",
      "status",
      "actions"
    ]
  };
  protectionTableConfig: ProtectionTableConfigInterface = {
    id: TableConfigType.Protection,
    limit: 10,
    offset: 1,
    length: 0,
    data: [],
    displayedColumns: ["type", "title", "insurer", "status", "actions"]
  };
  protected override sections = [
    { id: "partner-details", label: "Partner Details" },
    { id: "partner-branding", label: "Partner's Branding" },
    { id: "branch-management", label: "Branch Management" },
    { id: "user-management", label: "User Management" },
    { id: "protection-setup", label: "Protection Setup" }
  ];
  protected readonly PartnerStatus = PartnerStatus;

  constructor(
    protected alertService: AlertService,
    protected fb: FormBuilder,
    protected o2oIndustrySectorService: O2OIndustrySectorService,
    protected o2oPartnerService: O2OPartnerService,
    protected router: Router,
    protected route: ActivatedRoute,
    private dialog: MatDialog,
    private o2oPartnerBranchService: O2OPartnerBranchService,
    private o2oPartnerUserService: O2OPartnerUserService,
    private o2oPartnerProtectionService: O2OPartnerProtectionService
  ) {
    super(
      alertService,
      fb,
      o2oIndustrySectorService,
      o2oPartnerService,
      router,
      route
    );
  }

  get isFormChanged(): boolean {
    if (!this.details) return false;

    const {
      name,
      industrySector,
      subSector,
      registrationNo,
      urlSlug,
      themeColor,
      logo,
      banner,
      documents
    } = this.form.value;

    const isBasicInfoChanged =
      name !== this.details.name ||
      industrySector !== this.details.industrySectorId ||
      subSector !== this.details.subIndustrySectorId ||
      registrationNo !== this.details.registrationNo ||
      urlSlug !== this.details.urlSlug ||
      themeColor !== this.details.themeColor;

    const isFilesChanged =
      (logo && logo.length > 0) ||
      (banner && banner.length > 0) ||
      (documents && documents.length > 0);

    const isExistingDocumentsDeleted = this.details.companyDocuments?.some(
      (doc) => doc.UI_isDeleted
    );

    return isBasicInfoChanged || isFilesChanged || isExistingDocumentsDeleted;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setupFormValidators();
    this.setupValueChangesSubscriptions();
    this.fetchInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openBranchDialog(id?: string): void {
    const branch = id
      ? this.branchTableConfig.data.find((b) => b.id === id)
      : null;
    const dialogRef = this.dialog.open(AddBranchDialogComponent, {
      width: "500px",
      data: {
        id: this.id,
        branch
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getPartnerBranchListing$().subscribe();
      }
    });
  }

  openUserDialog(id?: string): void {
    const user = id ? this.userTableConfig.data.find((u) => u.id === id) : null;
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: "500px",
      data: {
        id: this.id,
        user
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getPartnerUserListing$().subscribe();
      }
    });
  }

  openProtectionDialog(id?: string): void {
    const protection = id
      ? this.protectionTableConfig.data.find((p) => p.id === id)
      : null;
    const dialogRef = this.dialog.open(AddProtectionDialogComponent, {
      width: "600px",
      data: {
        id: this.id,
        protection
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getPartnerProtectionListing$().subscribe();
      }
    });
  }

  toggleDeletion(doc: O2ODocumentInterface): void {
    doc.UI_isDeleted = !doc.UI_isDeleted;
  }

  viewImage(url: string): void {
    this.dialog.open(ImageViewerDialogComponent, {
      data: { url },
      width: "80%",
      maxWidth: "1000px",
      disableClose: true
    });
  }

  onBranchPageChange(e: PageEvent): void {
    this.branchTableConfig = {
      ...this.branchTableConfig,
      limit: e.pageSize,
      offset: e.pageIndex + 1
    };
    this.getPartnerBranchListing$().subscribe();
  }

  deleteBranch(id: string): void {
    const branch = this.branchTableConfig.data.find((b) => b.id === id);
    const data: ConfirmationDialogModel = {
      title: "Delete Branch",
      content: "Are you sure you want to delete branch",
      boldContent: branch?.name,
      okBtnText: "Delete",
      btnColor: "warn",
      hideQuestionMark: true
    };

    const dialogRef = this.dialog.open(ConfirmationDialog, { data });

    dialogRef.componentInstance.confirm.subscribe(() => {
      this.o2oPartnerBranchService.delete(this.id, id).subscribe({
        next: () => {
          this.alertService.openSnackBar("Branch deleted successfully");
          this.getPartnerBranchListing$().subscribe();
        },
        error: (err) => this.alertService.openSnackBar(err.error)
      });
    });
  }

  onUserPageChange(e: PageEvent): void {
    this.userTableConfig = {
      ...this.userTableConfig,
      limit: e.pageSize,
      offset: e.pageIndex + 1
    };
    this.getPartnerUserListing$().subscribe();
  }

  deleteUser(id: string): void {
    const user = this.userTableConfig.data.find((u) => u.id === id);
    const data: ConfirmationDialogModel = {
      title: "Delete User",
      content: "Are you sure you want to delete user",
      boldContent: user?.name,
      okBtnText: "Delete",
      btnColor: "warn",
      hideQuestionMark: true
    };

    const dialogRef = this.dialog.open(ConfirmationDialog, { data });

    dialogRef.componentInstance.confirm.subscribe(() => {
      this.o2oPartnerUserService.delete(this.id, id).subscribe({
        next: () => {
          this.alertService.openSnackBar("User deleted successfully");
          this.getPartnerUserListing$().subscribe();
        },
        error: (err) => this.alertService.openSnackBar(err.error)
      });
    });
  }

  sendEmail(id: string): void {
    this.o2oPartnerUserService.sendWelcomeEmail(this.id, id).subscribe({
      next: () => this.alertService.openSnackBar("Email sent successfully"),
      error: (err) => this.alertService.openSnackBar(err.error)
    });
  }

  onProtectionPageChange(e: PageEvent): void {
    this.protectionTableConfig = {
      ...this.protectionTableConfig,
      limit: e.pageSize,
      offset: e.pageIndex + 1
    };
    this.getPartnerProtectionListing$().subscribe();
  }

  deleteProtection(id: string): void {
    const protection = this.protectionTableConfig.data.find((p) => p.id === id);
    const data: ConfirmationDialogModel = {
      title: "Delete Protection",
      content: "Are you sure you want to delete protection",
      boldContent: protection?.title,
      okBtnText: "Delete",
      btnColor: "warn",
      hideQuestionMark: true
    };

    const dialogRef = this.dialog.open(ConfirmationDialog, { data });

    dialogRef.componentInstance.confirm.subscribe(() => {
      this.o2oPartnerProtectionService.delete(this.id, id).subscribe({
        next: () => {
          this.alertService.openSnackBar("Protection deleted successfully");
          this.getPartnerProtectionListing$().subscribe();
        },
        error: (err) => this.alertService.openSnackBar(err.error)
      });
    });
  }

  override savePartner() {
    if (this.form.invalid || !this.isFormChanged) return;

    const payload = this.getEditPayload();

    this.o2oPartnerService.update(this.id, payload).subscribe({
      next: () => {
        this.alertService.openSnackBar("Partner updated successfully");
        this.fetchInitialData();
        this.resetFileFormControls();
      },
      error: (err) => this.alertService.openSnackBar(err.error)
    });
  }

  private getEditPayload(): O2OPartnerEditInterface {
    const {
      name,
      subSector,
      registrationNo,
      urlSlug,
      themeColor,
      logo,
      banner,
      documents
    } = this.form.value;

    const payload: O2OPartnerEditInterface = {};

    if (name !== this.details.name) payload.name = name;
    if (subSector !== this.details.subIndustrySectorId)
      payload.industrySectorId = subSector;
    if (registrationNo !== this.details.registrationNo)
      payload.registrationNo = registrationNo;
    if (urlSlug !== this.details.urlSlug) payload.urlSlug = urlSlug;
    if (themeColor !== this.details.themeColor) payload.themeColor = themeColor;

    this.addBrandingToPayload(payload, logo, banner);
    this.addDocumentsToPayload(payload, documents);

    return payload;
  }

  private addBrandingToPayload(
    payload: O2OPartnerEditInterface,
    logo: File[],
    banner: File[]
  ): void {
    if (logo?.length > 0) {
      payload.logo = logo;
      if (this.details.logo?.documentId)
        payload.removedLogoId = this.details.logo.documentId;
    }

    if (banner?.length > 0) {
      payload.banner = banner;
      if (this.details.banner?.documentId)
        payload.removedBannerId = this.details.banner.documentId;
    }
  }

  private addDocumentsToPayload(
    payload: O2OPartnerEditInterface,
    documents: File[]
  ): void {
    if (documents?.length > 0) payload.companyDocuments = documents;

    const removedIds = this.details.companyDocuments
      ?.filter((doc) => doc.UI_isDeleted)
      .map((doc) => doc.documentId);

    if (removedIds?.length > 0) payload.removedCompanyDocumentIds = removedIds;
  }

  private resetFileFormControls(): void {
    ["logo", "banner", "documents"].forEach((control) =>
      this.form.get(control).setValue([])
    );
  }

  private setupFormValidators(): void {
    const controls = ["logo", "banner", "documents"];
    controls.forEach((controlName) => {
      const control = this.form.get(controlName);
      control.clearValidators();
      control.setValidators([ValidatorFunction.fileSize(5)]);
      control.updateValueAndValidity();
    });
  }

  private setupValueChangesSubscriptions(): void {
    const controls = ["logo", "banner"];
    controls.forEach((controlName) => {
      this.form
        .get(controlName)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (this.details && this.details[controlName]) {
            this.details[controlName].UI_isDeleted = !!(
              value && value.length > 0
            );
          }
        });
    });
  }

  private fetchInitialData(): void {
    forkJoin([
      this.getPartnerById$(),
      this.getPartnerBranchListing$(),
      this.getPartnerUserListing$(),
      this.getPartnerProtectionListing$()
    ]).subscribe({
      error: (err) => this.alertService.openSnackBar(err.error)
    });
  }

  private getPartnerById$() {
    return this.o2oPartnerService.getById(this.id).pipe(
      tap({
        next: (res) => {
          this.details = res;

          this.form.patchValue({
            name: res.name,
            industrySector: res.industrySectorId,
            subSector: res.subIndustrySectorId,
            urlSlug: res.urlSlug,
            themeColor: res.themeColor,
            registrationNo: res.registrationNo
          });
        }
      })
    );
  }

  private getPartnerBranchListing$() {
    return this.o2oPartnerBranchService
      .getListing(
        this.id,
        this.branchTableConfig.limit,
        this.branchTableConfig.offset
      )
      .pipe(
        tap({
          next: (res) => {
            this.branchTableConfig = {
              ...this.branchTableConfig,
              length: res.count,
              data: res.data
            };
          }
        })
      );
  }

  private getPartnerUserListing$() {
    return this.o2oPartnerUserService
      .getListing(
        this.id,
        this.userTableConfig.limit,
        this.userTableConfig.offset
      )
      .pipe(
        tap({
          next: (res) => {
            this.userTableConfig = {
              ...this.userTableConfig,
              length: res.count,
              data: res.data
            };
          }
        })
      );
  }

  private getPartnerProtectionListing$() {
    return this.o2oPartnerProtectionService
      .getListing(
        this.id,
        this.protectionTableConfig.limit,
        this.protectionTableConfig.offset
      )
      .pipe(
        tap({
          next: (res) => {
            this.protectionTableConfig = {
              ...this.protectionTableConfig,
              length: res.count,
              data: res.data
            };
          }
        })
      );
  }
}

export interface BranchTableConfigInterface {
  id: TableConfigType;
  limit: number;
  offset: number;
  length: number;
  data: O2OPartnerBranchListingInterface[];
  displayedColumns: string[];
}

export interface UserTableConfigInterface {
  id: TableConfigType;
  limit: number;
  offset: number;
  length: number;
  data: O2OPartnerUserListingInterface[];
  displayedColumns: string[];
}

export interface ProtectionTableConfigInterface {
  id: TableConfigType;
  limit: number;
  offset: number;
  length: number;
  data: O2OPartnerProtectionListingInterface[];
  displayedColumns: string[];
}

export type TableConfigInterface =
  | BranchTableConfigInterface
  | UserTableConfigInterface
  | ProtectionTableConfigInterface;

export type TableConfigDataInterface =
  | O2OPartnerUserListingInterface
  | O2OPartnerBranchListingInterface
  | O2OPartnerProtectionListingInterface;

export enum TableConfigType {
  Branch = "branch",
  User = "user",
  Protection = "protection"
}